import { EObservationTimelineChange } from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { ObservationTimelineEntryType } from '@/api/generated/api.enum';
import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useRefreshSetupChecklist } from '@/composables/setupChecklist/useRefreshSetupChecklist';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { invalidatePerformanceAssessmentEntryQueries } from './performanceAssessmentEntryQueryInvalidation';

interface IEntryMutation {
  schoolClassId: number;
  entryId: number;
}

/** Actions receive the entry's class explicitly and never subscribe to class lists. */
export function usePerformanceAssessmentEntryMutations() {
  const queryClient = useQueryClient();
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, showError } = useToast();
  const { trackEvent } = useAnalytics();
  const { refreshSetupChecklist } = useRefreshSetupChecklist();
  const onError = (error: Error): void => showError(error);
  const onEdited = async (
    _data: void | Components.Schemas.PerformanceAssessmentEntryDto,
    { schoolClassId }: IEntryMutation,
  ): Promise<void> => {
    await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId);
    editSuccessToast();
    trackEvent(AnalyticsEvents.performanceAssessment.entryUpdated);
  };

  const update = useMutation({
    mutationFn: async ({
      entryId,
      dto,
    }: IEntryMutation & { dto: Components.Schemas.UpdatePerformanceAssessmentEntryDto }) => {
      await Api.Routes.PerformanceAssessmentEntry.update({ id: entryId }, dto);
      if (dto.externalMaterialIds !== undefined) {
        await queryClient.invalidateQueries({ queryKey: ['supportMeasureAppRecommendations'] });
      }
    },
    onSuccess: async (_data, { schoolClassId, entryId, dto }) => {
      const contentOnly =
        dto.date === undefined && dto.externalMaterialIds === undefined && dto.gradeCalculationTagId === undefined;
      await invalidatePerformanceAssessmentEntryQueries(
        queryClient,
        schoolClassId,
        contentOnly
          ? {
              kind: EObservationTimelineChange.Update,
              entity: { type: ObservationTimelineEntryType.performanceAssessmentEntry, id: entryId },
            }
          : undefined,
      );
      editSuccessToast();
      trackEvent(AnalyticsEvents.performanceAssessment.entryUpdated);
    },
    onError,
  });
  const addCompetences = useMutation({
    mutationFn: async ({ entryId, dto }: IEntryMutation & { dto: Components.Schemas.AddCompetencesToEntryDto }) => {
      await Api.Routes.PerformanceAssessmentEntry.addCompetences({ id: entryId }, dto);
    },
    onSuccess: onEdited,
    onError,
  });
  const removeCompetence = useMutation({
    mutationFn: async ({ entryId, competenceId }: IEntryMutation & { competenceId: number }) => {
      await Api.Routes.PerformanceAssessmentEntry.removeCompetence({ id: entryId, competenceId });
    },
    onSuccess: onEdited,
    onError,
  });
  const setCompetences = useMutation({
    mutationFn: async ({
      entryId,
      dto,
    }: IEntryMutation & { dto: Components.Schemas.SetPerformanceAssessmentEntryCompetencesDto }) => {
      const response = await Api.Routes.PerformanceAssessmentEntry.setCompetences({ id: entryId }, dto);
      return response.data;
    },
    onSuccess: onEdited,
    onError,
  });
  const duplicate = useMutation({
    mutationFn: async ({
      entryId,
      studentIds,
      includeGradings,
    }: Components.Schemas.DuplicatePerformanceAssessmentEntryDto & { schoolClassId: number }) => {
      const response = await Api.Routes.PerformanceAssessmentEntry.duplicate(null, {
        entryId,
        studentIds,
        includeGradings,
      });
      return response.data;
    },
    onSuccess: async (_entry, { schoolClassId }) => {
      await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId);
      await refreshSetupChecklist();
      createSuccessToast();
      trackEvent(AnalyticsEvents.performanceAssessment.entryDuplicated);
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: async ({ entryId }: IEntryMutation) => {
      await Api.Routes.PerformanceAssessmentEntry.delete({ id: entryId });
    },
    onSuccess: async (_data, { schoolClassId, entryId }) => {
      await invalidatePerformanceAssessmentEntryQueries(queryClient, schoolClassId, {
        kind: EObservationTimelineChange.Delete,
        entity: { type: ObservationTimelineEntryType.performanceAssessmentEntry, id: entryId },
      });
      await refreshSetupChecklist();
      deleteSuccessToast();
      trackEvent(AnalyticsEvents.performanceAssessment.entryDeleted);
    },
    onError,
  });

  return {
    updatePerformanceAssessmentEntry: update.mutateAsync,
    addCompetencesToPerformanceAssessmentEntry: addCompetences.mutateAsync,
    removeCompetenceFromPerformanceAssessmentEntry: removeCompetence.mutateAsync,
    setCompetencesOnPerformanceAssessmentEntry: setCompetences.mutateAsync,
    duplicatePerformanceAssessmentEntry: duplicate.mutateAsync,
    deletePerformanceAssessmentEntry: remove.mutateAsync,
  };
}
